import { createAction, createReducer, on, props } from '@ngrx/store';
import { UserInitState } from '../models/user-reducer.interface';

// Define actions using createAction
export const UPDATE_USER = createAction(
  '[User] Update User',
  props<{ currentUser: any }>()
);

export const UPDATE_TOKEN = createAction(
  '[User] Update Token',
  props<{ userToken: string }>()
);

export const UPDATE_TOKEN_VALID = createAction(
  '[User] Update Token Valid',
  props<{ userTokenValid: boolean }>()
);

export const UPDATE_USER_LOGGED_IN = createAction(
  '[User] Update User Logged In',
  props<{ userLoggedIn: boolean }>()
);

export const UPDATE_USER_SHOW_TOAST = createAction(
  '[User] Update User Show Toast',
  props<{ userShowToast: boolean }>()
);

export const UPDATE_USER_HAS_PASSWORD = createAction(
  '[User] Update User Has Password',
  props<{ userHasPassword: boolean }>()
);

export const UPDATE_USER_EMAIL_TOKEN = createAction(
  '[User] Update User Email Token',
  props<{ userEmailToken: any }>()
);

export const UPDATE_USER_EMAIL_TOKEN_VALID = createAction(
  '[User] Update User Email Token Valid',
  props<{ userEmailTokenValid: boolean }>()
);

export const UPDATE_USER_EMAIL = createAction(
  '[User] Update User Email',
  props<{ userEmail: string }>()
);

export const UPDATE_USER_ROLE = createAction(
  '[User] Update User Role',
  props<{ userRole: string }>()
);

export const UPDATE_USER_ADDRESSES = createAction(
  '[User] Update User Addresses',
  props<{ userAddresses: any[] }>()
);

export const UPDATE_USER_PERMISSIONS = createAction(
  '[User] Update User Permissions',
  props<{ userPermissions: any }>()
);

export const UPDATE_USER_STATUS = createAction(
  '[User] Update User Status',
  props<{ userStatus: number }>()
);

export const UPDATE_REDIRECT = createAction(
  '[User] Update Redirect',
  props<{ redirect: any }>()
);

export const UPDATE_ADMIN_USERS = createAction(
  '[User] Update Admin Users',
  props<{ adminUsers: any }>()
);

export const UPDATE_AMEND_CARDS = createAction(
  '[User] Update Amend Cards',
  props<{ amendUserCards: any }>()
);

export const UPDATE_NEW_USER = createAction(
  '[User] Update New User',
  props<{ newUser: any }>()
);

export const UPDATE_MARKETING_ENROLMENT = createAction(
  '[User] Update Marketing Enrolment',
  props<{ hasMarketingEnrolment: boolean }>()
);

export const UPDATE_SURVEY_MARKETING_ENROLMENT = createAction(
  '[User] Update Survey Marketing Enrolment',
  props<{ hasSurveyMarketingEnrolment: boolean }>()
);

export const REFRESH_USER_STATE = createAction(
  '[User] Refresh User State',
  props<{ refreshObj: any }>()
);

export const RESET_USER_STATE = createAction(
  '[User] Reset User State'
);

// Initial state
const initState: UserInitState = {
  userDetails: {
    currentUser: {},
    userToken: '',
    userTokenValid: false,
    userLoggedIn: false,
    userShowToast: false,
    userHasPassword: false,
    userEmailToken: {},
    userEmailTokenValid: true,
    userEmail: '',
    userRole: '',
    userAddresses: [],
    userPermissions: {
      showAdminFeatures: false,
    },
    userStatus: 0,
    redirect: { path: '', ref: '' },
    adminUsers: {
      listOfUsers: null,
    },
    amendUserCards: null,
    newUser: null,
    hasMarketingEnrolment: true,
    hasSurveyMarketingEnrolment: false,
  },
};

// Reducer using createReducer and on
export const userReducer = createReducer(
  initState,

  // Action handlers
  on(UPDATE_USER, (state, { currentUser }) => ({
    ...state,
    userDetails: { ...state.userDetails, currentUser },
  })),
  on(UPDATE_TOKEN, (state, { userToken }) => ({
    ...state,
    userDetails: { ...state.userDetails, userToken },
  })),
  on(UPDATE_TOKEN_VALID, (state, { userTokenValid }) => ({
    ...state,
    userDetails: { ...state.userDetails, userTokenValid },
  })),
  on(UPDATE_USER_LOGGED_IN, (state, { userLoggedIn }) => ({
    ...state,
    userDetails: { ...state.userDetails, userLoggedIn },
  })),
  on(UPDATE_USER_SHOW_TOAST, (state, { userShowToast }) => ({
    ...state,
    userDetails: { ...state.userDetails, userShowToast },
  })),
  on(UPDATE_USER_HAS_PASSWORD, (state, { userHasPassword }) => ({
    ...state,
    userDetails: { ...state.userDetails, userHasPassword },
  })),
  on(UPDATE_USER_EMAIL_TOKEN, (state, { userEmailToken }) => ({
    ...state,
    userDetails: { ...state.userDetails, userEmailToken },
  })),
  on(UPDATE_USER_EMAIL_TOKEN_VALID, (state, { userEmailTokenValid }) => ({
    ...state,
    userDetails: { ...state.userDetails, userEmailTokenValid },
  })),
  on(UPDATE_USER_EMAIL, (state, { userEmail }) => ({
    ...state,
    userDetails: { ...state.userDetails, userEmail },
  })),
  on(UPDATE_USER_ROLE, (state, { userRole }) => ({
    ...state,
    userDetails: { ...state.userDetails, userRole },
  })),
  on(UPDATE_USER_ADDRESSES, (state, { userAddresses }) => ({
    ...state,
    userDetails: { ...state.userDetails, userAddresses },
  })),
  on(UPDATE_USER_PERMISSIONS, (state, { userPermissions }) => ({
    ...state,
    userDetails: { ...state.userDetails, userPermissions },
  })),
  on(UPDATE_USER_STATUS, (state, { userStatus }) => ({
    ...state,
    userDetails: { ...state.userDetails, userStatus },
  })),
  on(UPDATE_REDIRECT, (state, { redirect }) => ({
    ...state,
    userDetails: { ...state.userDetails, redirect },
  })),
  on(UPDATE_ADMIN_USERS, (state, { adminUsers }) => ({
    ...state,
    userDetails: { ...state.userDetails, adminUsers },
  })),
  on(UPDATE_AMEND_CARDS, (state, { amendUserCards }) => ({
    ...state,
    userDetails: { ...state.userDetails, amendUserCards },
  })),
  on(UPDATE_NEW_USER, (state, { newUser }) => ({
    ...state,
    userDetails: { ...state.userDetails, newUser },
  })),
  on(UPDATE_MARKETING_ENROLMENT, (state, { hasMarketingEnrolment }) => ({
    ...state,
    userDetails: { ...state.userDetails, hasMarketingEnrolment },
  })),
  on(UPDATE_SURVEY_MARKETING_ENROLMENT, (state, { hasSurveyMarketingEnrolment }) => ({
    ...state,
    userDetails: { ...state.userDetails, hasSurveyMarketingEnrolment },
  })),
  on(REFRESH_USER_STATE, (state, { refreshObj }) => ({
    ...state,
    userDetails: { ...refreshObj.userDetails },
  })),
  on(RESET_USER_STATE, () => ({
    ...initState,
  }))
);
